import { useCallback, useEffect, useRef, useState } from "react";
import LoadingSpinner from "../../components/Loader";
import styles from "../../assets/css/remittance.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteBridgeData,
  fetchLinkToken,
  goBack,
  reset,
  setLogout,
  toggleBackBtnVisiblity,
  updateBridgeData,
  updateNbStatusInfo,
} from "../../redux/slices/remittanceSlice";
import isEmpty from "lodash/isEmpty";
import AppModal from "../../components/modal/AppModal";
import { useNavigate } from "react-router-dom";
import {
  FEATURE_NOT_AVAILABLE_MESSAGE,
  REMITTANCE_NB_HOME_PATH,
} from "../../constants";
import { postMparticleEvent } from "../../libs/events";
import FeatureNotAvailable from "../../components/modal/FeatureNotAvailable";
import { userLogout } from "../../libs/auth";
import { startLoader, stopLoader } from "../../redux/slices/appSlice";
import SecurityUpgrade from "../../components/modal/SecurityUpgrade";
import { formatUsNumber } from "../../libs/utils";

const Remittance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const iframeRef = useRef();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [error, setError] = useState({});
  const [featureNaMessage, setFeatureNaMessage] = useState(null);
  const [phone, setPhone] = useState("");

  const { loader, isMobileDevice } = useSelector((state) => state.app);
  const { bridgeData, back, isLogout } = useSelector(
    (state) => state.remittance
  );
  const fingerprint = useSelector((state) => state.fingerprint);
  const auth = useSelector((state) => state.auth);

  const sendMessage = (data = {}) => {
    if (!iframeRef.current) {
      return;
    }
    if (iframeLoaded) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify(data),
        process.env.REACT_APP_NEOBANK_URL
      );
    }
  };

  useEffect(() => {
    if (back) {
      sendMessage({
        action: "GO_TO_BACK",
      });
      dispatch(goBack(false));
    }
  }, [back]);

  useEffect(() => {
    if (isLogout) {
      dispatch(startLoader());
      sendMessage({
        action: "NB_LOG_OUT",
      });
      dispatch(setLogout(false));
    }
  }, [isLogout]);

  const handleActions = useCallback((data) => {
    switch (data.action) {
      case "UPDATE_NB_INFO":
        dispatch(updateNbStatusInfo(data));
        break;
      case "AUTHENTICATE_NB":
        // dispatch(reset());
        dispatch(toggleBackBtnVisiblity(false));
        navigate("/re-authenticate", {
          replace: true,
          state: { referee: REMITTANCE_NB_HOME_PATH },
        });
        break;
      case "TOGGLE_BACK_BUTTON":
        dispatch(toggleBackBtnVisiblity(data.show));
        break;
      case "NB_LOG_OUT_SUCCESS":
        userLogout(navigate);
        dispatch(stopLoader());
        break;
      case "POST_EVENT":
        postMparticleEvent(data);
        break;
      case "REMITTANCE_WEB_BLOCKED_FEATURE":
        setFeatureNaMessage({ header: data.featureBlockType });
        break;
      case "BANK_LINK":
        dispatch(updateBridgeData(data.data));
        navigate("/accounts?type=addBankFromNB", {
          replace: true,
        });
        break;
      case "CLOSE_WEB_REMITTANCE":
        navigate("/accounts", {
          replace: true,
        });
        break;
    }
  }, []);

  const handlePostMessageEvent = useCallback((e) => {
    if (e.origin !== process.env.REACT_APP_NEOBANK_URL) return;
    handleActions(e.data);
  });

  useEffect(() => {
    // window.addEventListener("popstate", (event) => {
    //   alert("You message");
    // });
    let isListenerAttached = false;
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = function () {
      window.history.pushState(null, "", window.location.href);
    };
    if (isListenerAttached) return;
    isListenerAttached = true;
    window.addEventListener("message", handlePostMessageEvent, true);
    return () =>
      window.removeEventListener("message", handlePostMessageEvent, true);
  }, []);

  useEffect(() => {
    if (!isEmpty(fingerprint.data)) {
      if (bridgeData?.metaInfo) {
        return;
      }
      dispatch(fetchLinkToken());
    }
  }, [fingerprint.data]);

  useEffect(() => {
    if (
      !isEmpty(bridgeData) &&
      !isEmpty(auth) &&
      iframeLoaded &&
      !isEmpty(fingerprint.data)
    ) {
      if (bridgeData?.metaInfo) {
        if (!bridgeData?.status) {
          return;
        }
        sendMessage(bridgeData);
        dispatch(deleteBridgeData());
        return;
      }
      if (!bridgeData?.success) {
        if (bridgeData?.error?.code === 7020) {
          const { user: { data: { user: { mobile_number } = {} } = {} } = {} } =
            auth;
          setPhone(mobile_number);
        }
        setError(bridgeData.error);
        return;
      }
      if (!bridgeData?.linkToken || !bridgeData?.sso_token) {
        setError({
          message:
            "We’re sorry, but we couldn’t complete your request. Please try again later or contact us at support@joinabound.com.",
        });
        return;
      }
      const {
        user: {
          data: {
            user: { customer_id, first_name, last_name, email } = {},
          } = {},
        } = {},
      } = auth;
      const dataToPost = {
        deviceId: fingerprint.data,
        platform: "web",
        customerId: customer_id,
        productType: "REMITTANCE",
        ssoToken: bridgeData.sso_token,
        linkToken: bridgeData.linkToken,
        neoBankApiEndpoint: process.env.REACT_APP_API_ENDPOINT,
        deviceType: "web",
        isMobile: isMobileDevice,
        productType: "REMITTANCE",
        firstName: first_name || "",
        lastName: last_name || "",
        email,
      };
      sendMessage(dataToPost);
      dispatch(deleteBridgeData());
    }
  }, [
    bridgeData,
    auth,
    iframeLoaded,
    fingerprint.data,
    auth?.user?.data?.user?.customer_id,
  ]);

  const handleIframeLoaded = () => {
    setIframeLoaded(true);
  };

  const handleFeatureNotAvailableModalClose = () => {
    setFeatureNaMessage(null);
  };

  const { message, code } = error || {};

  const isMigrationPending = code === 7020;

  const dismissError = () => {
    if (isMigrationPending) {
      dispatch(deleteBridgeData());
      navigate("/accounts?migrateType=phone", { replace: true });
      return;
    }
    navigate(0, { replace: true });
  };

  const handleCancel = () => {
    navigate("/accounts", { replace: true });
  };

  return (
    <div
      className={
        isMobileDevice
          ? styles.remittanceMobContainer
          : styles.remittanceContainer
      }
    >
      <iframe
        src={process.env.REACT_APP_NEOBANK_URL}
        title="neobank-remittance"
        height="100%"
        width="100%"
        frameBorder={0}
        ref={iframeRef}
        onLoad={handleIframeLoaded}
      ></iframe>

      {isMigrationPending && (
        <AppModal
          modalClass={styles.modalClass}
          title="Security Upgrade!"
          action={dismissError}
          actionLabel={isMigrationPending ? "Continue" : "Retry"}
          isOptionalHeadingJSX
          headingJSX={<SecurityUpgrade phone={formatUsNumber(phone)} />}
          handleClose={handleCancel}
        />
      )}

      {!isMigrationPending && message && (
        <AppModal
          modalClass={styles.modalClass}
          title={message}
          handleClose={dismissError}
          cancelLabel="Retry"
        />
      )}
      {featureNaMessage && (
        <AppModal
          heading={FEATURE_NOT_AVAILABLE_MESSAGE}
          handleClose={handleFeatureNotAvailableModalClose}
          isCloseBtnRequired
          hasCustomBody
          headerWithCrossCls={styles.headerWithCrossCls}
          modalClass={styles.modalClass}
          crossIconHeight={isMobileDevice ? 30 : 40}
          crossIconWidth={isMobileDevice ? 30 : 40}
        >
          <FeatureNotAvailable header={featureNaMessage?.header} />
        </AppModal>
      )}
      {loader && <LoadingSpinner />}
    </div>
  );
};
export default Remittance;
